import { useAtomValue } from 'jotai';
import { DatabaseIcon } from 'lucide-react';
import React from 'react';

import { toSlug } from '@crossnokaye/ux-common';
import { ConditionalRender, Grid } from '@crossnokaye/ux-common';

import { EmptyStatePlaceholder } from '@/components/molecules/empty-state-placeholder';
import { displayMetricsListAtom } from '@/features/equipment-browser/equipment-details/state/display-metrics.state';
import { equipmentLoadingAtom } from '@/features/equipment-browser/equipment-details/state/equipment-response.state';
import {
  activeSequencerAtom,
  activeSequencerDisplayMetricsListAtom,
} from '@/features/equipment-browser/equipment-details/state/sequencers.state';
import { SectionTemplate } from '@/features/equipment-browser/equipment-details/templates/section-template';
import { SubSectionTemplate } from '@/features/equipment-browser/equipment-details/templates/subsection-template';
import { EnhancedReading } from '@/features/equipment-browser/subscriptions/use-get-readings-values';

import { EquipmentDetailsMetricLabel } from './equipment-details-metric-label';

export const EquipmentDetailsMetrics = (): React.ReactNode => {
  const activeSequencer = useAtomValue(activeSequencerAtom);
  const displayMetrics = useAtomValue(displayMetricsListAtom);
  const isLoading = useAtomValue(equipmentLoadingAtom);
  const activeSequencerDisplayMetrics = useAtomValue(activeSequencerDisplayMetricsListAtom);

  const hasNoMetrics = !displayMetrics || displayMetrics.length === 0;

  return (
    <SectionTemplate title={'Metrics'} icon={DatabaseIcon} isLoading={isLoading}>
      <SubSectionTemplate title={'General'} isLoading={isLoading}>
        {hasNoMetrics ? (
          <EmptyStatePlaceholder message="No metrics available" />
        ) : (
          <Grid variant="responsive-5" gap="thin">
            {displayMetrics?.map((reading: EnhancedReading, index: number) => {
              return (
                <EquipmentDetailsMetricLabel
                  key={toSlug(`general-metrics-${reading?.key}-${index}`)}
                  reading={reading}
                />
              );
            })}
          </Grid>
        )}
      </SubSectionTemplate>
      <ConditionalRender condition={!!activeSequencer && activeSequencerDisplayMetrics.length > 0}>
        <SubSectionTemplate
          title={`Sequencer (${activeSequencer?.displayName ?? 'Unknown Name'})`}
          isLoading={isLoading}
        >
          <Grid variant="responsive-5" gap="thin">
            {activeSequencerDisplayMetrics.map((reading: EnhancedReading, index: number) => (
              <EquipmentDetailsMetricLabel
                key={toSlug(`sequencer-metrics-${reading?.key}-${index}`)}
                reading={reading}
              />
            ))}
          </Grid>
        </SubSectionTemplate>
      </ConditionalRender>
    </SectionTemplate>
  );
};
